import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { ChatControls } from '../chat/ChatView'
import type { FanData } from '../chat/useFan'
import { FeatherIcon } from '../components/FeatherIcon'
import { useHeaderActions, type HeaderActions } from './headerActions'
import { RenameDialog } from './RenameDialog'
import { Spinner } from '../components/Spinner'

export interface HeaderPermissions {
  /** Chats write: rename, mute, hide, clear, like. */
  writeChats: boolean
  /** Limit (users/restrict: Chats, Collections or Profile write). */
  restrict: boolean
  /** Collections write: the list star. */
  editLists: boolean
}

interface Props {
  accountId: string
  fanId: string
  fan: FanData
  chat: ChatControls
  nameHint: string | null
  can: HeaderPermissions
  onLeave: () => void
  onBack: (() => void) | null
}

/** The dialogs a header opens: the confirm before an OnlyFans write, and rename. */
export function HeaderDialogs({ h, fanId }: { h: HeaderActions; fanId: string }) {
  const { t } = useTranslation('chats')
  return (
    <>
      {h.confirming && (
        <ConfirmDialog
          text={t(`more.${h.confirming}Confirm`)}
          action={t(`more.${h.confirming}`)}
          onCancel={() => h.setConfirming(null)}
          onConfirm={() => {
            const act = h.confirmed[h.confirming!]
            h.setConfirming(null)
            void act()
          }}
        />
      )}
      {h.renaming && (
        <RenameDialog
          official={h.official}
          handle={h.p?.username ? `@${h.p.username}` : `@fan_${fanId}`}
          current={h.custom}
          onClose={() => h.setRenaming(false)}
          onSave={h.saveName}
        />
      )}
    </>
  )
}

/** The CRM chat window's header (chats.js renderChatWindowHeaderHtml) for the open fan. */
export function ChatHeader({ accountId, fanId, fan, chat, nameHint, can, onLeave, onBack }: Props) {
  const { t } = useTranslation('chats')
  const h = useHeaderActions({ accountId, fanId, fan, nameHint, onLeave })
  const { p, name, presence, pinned, muted, notice, setNotice, togglePin, toggleMute } = h
  const [menu, setMenu] = useState(false)
  const [picking, setPicking] = useState(false)
  return (
    <div className="chat-window-header" role="group" aria-label={t('header.label', { name })}>
      {onBack && (
        <button type="button" className="chat-head-nav-btn chat-head-back-btn" aria-label={t('backToChats')} title={t('backToChats')} onClick={onBack}>
          <FeatherIcon name="arrow-left" size={22} />
        </button>
      )}
      <div className="chat-window-user">
        <div className="chat-window-name-row">
          <span className="chat-window-name">{name}</span>
          {p?.username && <span className="chat-window-handle">@{p.username}</span>}
          {can.writeChats && (
            <button type="button" className="chat-head-edit-name-btn" aria-label={t('rename.title')} title={t('rename.title')} onClick={() => h.setRenaming(true)}>
              <FeatherIcon name="edit-2" size={15} />
            </button>
          )}
          {p?.verified && (
            <span className="chat-window-verify" role="img" aria-label={t('header.verified')}>
              <FeatherIcon name="badge-check" size={17} />
            </span>
          )}
        </div>
        <div className="chat-window-actions chat-window-actions--sub">
          {presence && (
            <div className={`chat-head-sub-status ${presence.online ? 'is-online' : ''}`} role="status">
              <span className="chat-window-status-dot" aria-hidden="true" />
              <span className="chat-window-status">{presence.label}</span>
            </div>
          )}
          {can.editLists && (
            <span className="chat-head-tool-wrap">
              <HeadTool icon="star" label={t('lists.star')} on={fan.memberOf.size > 0} className="chat-list-btn" onClick={() => setPicking((v) => !v)} />
              {picking && <ListPicker fan={fan} onClose={() => setPicking(false)} />}
            </span>
          )}
          {can.writeChats && (
            <HeadTool icon={muted ? 'bell-off' : 'bell'} label={muted ? t('header.unmute') : t('header.mute')} on={muted} className="chat-mute-btn" onClick={() => void toggleMute()} />
          )}
          <HeadTool icon="pin" label={pinned ? t('header.unpin') : t('header.pin')} on={pinned} className="chat-pin-btn" onClick={togglePin} />
          <button type="button" className={`chat-head-tab-btn ${chat.gallery ? 'active' : ''}`} aria-label={t('header.gallery')} title={t('header.galleryHint')} aria-pressed={chat.gallery} onClick={chat.toggleGallery}>
            <FeatherIcon name="image" size={15} />
            <span className="chat-head-tab-label">{t('header.gallery')}</span>
          </button>
          <button type="button" className={`chat-head-tab-btn ${chat.finding ? 'active' : ''}`} aria-label={t('header.find')} title={t('header.find')} aria-pressed={chat.finding} onClick={chat.toggleFind}>
            <FeatherIcon name="search" size={15} />
            <span className="chat-head-tab-label">{t('header.findShort')}</span>
          </button>

        </div>
        {notice && (
          <div className="chat-head-notice" role="status">
            {notice}
            <button type="button" aria-label={t('common.close')} onClick={() => setNotice(null)}>
              <FeatherIcon name="x" size={12} />
            </button>
          </div>
        )}
      </div>
      <div className="chat-window-actions chat-window-actions--top">
        {/* Phones: the side column's sheet, beside More so it never scrolls out of the tool row. */}
        {chat.side && (
          <button type="button" className={`chat-head-icon-btn chat-side-btn ${chat.side.open ? 'active' : ''}`} aria-label={t('header.fanInfo')} title={t('header.fanInfo')} aria-pressed={chat.side.open} onClick={chat.side.toggle}>
            <FeatherIcon name="panel-right-open" size={20} />
          </button>
        )}
        <button type="button" className="chat-head-icon-btn chat-more-btn" aria-label={t('more.title')} title={t('more.title')} aria-expanded={menu} onClick={() => setMenu((v) => !v)}>
          <FeatherIcon name="more-vertical" size={20} />
        </button>
        {menu && (
          <MoreMenu onClose={() => setMenu(false)}>
            <MenuItem label={t('more.refresh')} onClick={chat.refresh} />
            {can.writeChats && <MenuItem label={t('more.hide')} onClick={() => h.setConfirming('hide')} />}
            {can.restrict && <MenuItem label={t('more.restrict')} onClick={() => h.setConfirming('restrict')} />}
            {can.writeChats && <MenuItem label={t('more.clear')} danger onClick={() => h.setConfirming('clear')} />}
          </MoreMenu>
        )}
      </div>
      <HeaderDialogs h={h} fanId={fanId} />
    </div>
  )
}

/** One of the header's small tools under the name (chat-head-tool-btn); `on` = highlighted. */
function HeadTool({ icon, label, on, className = '', onClick }: { icon: string; label: string; on: boolean; className?: string; onClick: () => void }) {
  return (
    <button type="button" className={`chat-head-tool-btn ${className} ${on ? 'active' : ''}`} aria-label={label} title={label} aria-pressed={on} onClick={onClick}>
      <FeatherIcon name={icon} size={15} />
    </button>
  )
}

/** The ⋮ menu (chat-more-menu); closes on a pick, an outside click or Escape. */
function MoreMenu({ onClose, children }: { onClose: () => void; children: React.ReactNode }) {
  const box = useRef<HTMLDivElement>(null)
  useDismiss(box, onClose)
  return (
    <div className="chat-more-menu" role="menu" ref={box} onClick={onClose}>
      {children}
    </div>
  )
}

function MenuItem({ label, danger = false, onClick }: { label: string; danger?: boolean; onClick: () => void }) {
  return (
    <button type="button" role="menuitem" className={danger ? 'danger' : ''} onClick={onClick}>
      {label}
    </button>
  )
}

/** The CRM's small confirm (chat-clear-modal): the question, Cancel and the action. */
export function ConfirmDialog({ text, action, onConfirm, onCancel }: { text: string; action: string; onConfirm: () => void; onCancel: () => void }) {
  const { t } = useTranslation('chats')
  return (
    <div className="cp-modal-backdrop" onMouseDown={(e) => e.target === e.currentTarget && onCancel()}>
      <div className="cp-modal cp-modal--confirm" role="alertdialog" aria-modal="true" aria-label={text}>
        <p className="cp-modal-text">{text}</p>
        <div className="cp-modal-actions">
          <button type="button" className="cp-btn" autoFocus onClick={onCancel}>
            {t('common.cancel')}
          </button>
          <button type="button" className="cp-btn cp-btn--danger" onClick={onConfirm}>
            {action}
          </button>
        </div>
      </div>
    </div>
  )
}

/** "Save to list" (ui.js openFanListPopover): the account's lists, ticked where the fan is. */
export function ListPicker({ fan, onClose }: { fan: FanData; onClose: () => void }) {
  const { t } = useTranslation('chats')
  const box = useRef<HTMLDivElement>(null)
  const [busy, setBusy] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  useDismiss(box, onClose)
  const toggle = async (listId: string, member: boolean) => {
    setBusy(listId)
    setError(null)
    try {
      await fan.setMembership(listId, member)
    } catch (err) {
      setError((err as Error).message)
    } finally {
      setBusy(null)
    }
  }
  return (
    <div className="chat-list-picker" role="dialog" aria-label={t('lists.title')} ref={box}>
      <div className="chat-list-picker-title">{t('lists.title')}</div>
      {!fan.lists.length && <div className="chat-list-picker-empty">{fan.loading ? <Spinner size={14} label={t('lists.loading')} /> : t('lists.none')}</div>}
      <div className="chat-list-picker-rows">
        {fan.lists.map((l) => (
          <label key={l.id} className="chat-list-picker-row">
            <input type="checkbox" checked={fan.memberOf.has(l.id)} disabled={busy != null} onChange={(e) => void toggle(l.id, e.target.checked)} />
            <span>{l.name}</span>
          </label>
        ))}
      </div>
      {error && <div className="chat-list-picker-error">{error}</div>}
    </div>
  )
}

/** Closes a popover on a click outside it and its opener (the parent), or on Escape. */
function useDismiss(box: React.RefObject<HTMLElement | null>, onClose: () => void) {
  useEffect(() => {
    const away = (e: MouseEvent) => !box.current?.parentElement?.contains(e.target as Node) && onClose()
    const esc = (e: KeyboardEvent) => e.key === 'Escape' && onClose()
    window.addEventListener('mousedown', away)
    window.addEventListener('keydown', esc)
    return () => {
      window.removeEventListener('mousedown', away)
      window.removeEventListener('keydown', esc)
    }
  }, [box, onClose])
}
